import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { FeatureCutout } from './FeatureCutouts';
import { whatsNewFeatures, type WhatsNewFeature } from '../../data/whatsNew';
type WhatsNewOverlayProps = {
  onClose: () => void;
};
const ease = [0.23, 1, 0.32, 1] as const;

/** Grid placement by importance: Protect leads, then the prep that builds on it. */
const cellLayout: Record<WhatsNewFeature['id'], {
  cell: string;
  stack: boolean;
}> = {
  protect: {
    cell: 'lg:col-span-7',
    stack: false
  },
  prepare: {
    cell: 'lg:col-span-5',
    stack: true
  },
  history: {
    cell: 'lg:col-span-4',
    stack: true
  },
  profiles: {
    cell: 'lg:col-span-4',
    stack: true
  },
  export: {
    cell: 'lg:col-span-4',
    stack: true
  }
};
export function WhatsNewOverlay({
  onClose
}: WhatsNewOverlayProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    closeRef.current?.focus({
      preventScroll: true
    });
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return <motion.div role="dialog" aria-modal="true" aria-labelledby="whats-new-title" initial={{
    clipPath: 'inset(0 0 100% 0)'
  }} animate={{
    clipPath: 'inset(0 0 0% 0)'
  }} exit={{
    clipPath: 'inset(0 0 100% 0)'
  }} transition={{
    duration: 0.3,
    ease
  }} className="fixed inset-x-0 bottom-0 top-[26px] z-[60] flex flex-col bg-[rgba(10,13,17,0.82)] backdrop-blur-[6px]" data-id="element-1005" data-mp="10uz0i1v0x986">
      
      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6 lg:px-10" data-id="element-1006" data-mp="d2qvpkbbmokl">
        <div className="mx-auto max-w-[1240px] border border-white/30 bg-[rgba(16,20,25,0.7)] backdrop-blur-[10px]" data-id="element-1007" data-mp="d54qds239yrm">
          <header className="flex items-center justify-between gap-6 border-b border-white/30 px-7 py-6" data-id="element-1008" data-mp="27h1i7m4g16n7">
            <div data-id="element-1009" data-mp="22i8720cmprq7">
              <h2 id="whats-new-title" className="text-[30px] font-extrabold leading-none tracking-[-0.01em] text-white" data-id="element-1010" data-mp="22hp0ffsfiprh">
                What changed?
              </h2>
              <p className="mt-2 text-[13px] text-white/80" data-id="element-1011" data-mp="2qda66jjh5ksf">New in this PreForm build, with where to find each feature.</p>
            </div>
            <button ref={closeRef} type="button" onClick={onClose} aria-label="Close what changed" className="flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-full border border-white/45 bg-white/10 text-white transition-[background-color,transform] duration-150 hover:bg-white/20 active:scale-[0.96] focus-visible:outline focus-visible:outline-2 focus-visible:outline-white" data-id="element-1012" data-mp="2cmybccvi97xl">
              
              <XIcon className="h-[22px] w-[22px]" strokeWidth={2.4} data-id="element-1013" data-mp="30aolugxkq5jo" />
            </button>
          </header>
          <ul className="grid grid-cols-1 gap-px bg-white/30 lg:grid-cols-12" data-id="element-1014" data-mp="1jw6jfrlh35g2">
            {whatsNewFeatures.map((f, i) => <FeatureCell key={f.id} feature={f} index={i} data-id="element-1015" data-mp="1oxvuv8clxl4o" data-mpi={i} />)}
          </ul>
        </div>
      </div>
    </motion.div>;
}
function FeatureCell({
  feature,
  index
}: {
  feature: WhatsNewFeature;
  index: number;
}) {
  const layout = cellLayout[feature.id];
  const lead = feature.id === 'protect';
  return <motion.li initial={{
    opacity: 0,
    y: 8
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.24,
    ease,
    delay: 0.12 + index * 0.05
  }} className={`flex flex-col gap-5 bg-[rgba(22,27,33,0.94)] p-7 ${layout.cell} ${lead ? 'lg:row-span-1' : ''}`} data-id="element-1016" data-mp="1qbdrokcdahnx">
      
      <div data-id="element-1017" data-mp="tosc57izqg04">
        <p className="text-[11.5px] font-medium text-[#9cc0ea]" data-id="element-1018" data-mp="tl4ga1q2phi8">{feature.location}</p>
        <h3 className={`mt-1 font-bold leading-tight text-white ${lead ? 'text-[22px]' : 'text-[17px]'}`} data-id="element-1019" data-mp="17g6tl9x92952">{feature.title}</h3>
        <p className={`mt-2 leading-relaxed text-white/85 ${lead ? 'max-w-[560px] text-[13.5px]' : 'text-[12.5px]'}`} data-id="element-1020" data-mp="12m2l2m8c5t1v">{feature.description}</p>
        <ul className="mt-3 flex flex-col gap-[5px]" data-id="element-1021" data-mp="9zzy55oof1hb">
          {feature.points.map(p => <li key={p} className="flex gap-[8px] text-[12px] leading-snug text-white/75" data-id="element-1022" data-mp="nmqcbyeif0q8">
              <span className="mt-[6px] h-[4px] w-[4px] shrink-0 rounded-full bg-white/60" aria-hidden="true" data-id="element-1023" data-mp="skihhu460jcd" />
              {p}
            </li>)}
        </ul>
      </div>
      <div className="mt-auto" data-id="element-1024" data-mp="12auyxdgonwea">
        <FeatureCutout id={feature.id} data-id="element-1025" data-mp="12b3exe83rhtp" />
      </div>
    </motion.li>;
}